import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { translations as DEFAULTS } from "../lib/i18n";
import { api } from "../lib/api";
import { trackLanguageChange } from "../lib/analytics";

const LanguageContext = createContext(null);

const deepMerge = (a, b) => {
  if (!b || typeof b !== "object") return a;
  const out = Array.isArray(a) ? [...a] : { ...a };
  for (const k of Object.keys(b)) {
    if (
      b[k] !== null &&
      typeof b[k] === "object" &&
      !Array.isArray(b[k]) &&
      a &&
      a[k] &&
      typeof a[k] === "object"
    ) {
      out[k] = deepMerge(a[k], b[k]);
    } else if (b[k] !== "" && b[k] !== undefined && b[k] !== null) {
      out[k] = b[k];
    }
  }
  return out;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "el");
  const [overrides, setOverrides] = useState({ el: {}, en: {} });
  const initialMount = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/translations");
      setOverrides({ el: r.data?.el || {}, en: r.data?.en || {} });
    } catch (error) {
      console.error("Failed to load translations:", error);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    trackLanguageChange(lang);
  }, [lang]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const t = useMemo(
    () => deepMerge(DEFAULTS[lang], overrides[lang] || {}),
    [lang, overrides]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, refresh }),
    [lang, t, refresh]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
};
